import { useCallback, useEffect, useMemo, useRef } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import { TaskItem } from '@tiptap/extension-task-item';
import { TaskList } from '@tiptap/extension-task-list';
import { howtoApi } from '@/features/howto/api';
import { FileAttachment } from '@/features/howto/editor/file-attachment';
import { SlashMenu, slashCommands } from '@/features/howto/editor/slash-menu';

/** 이미지로 보여 줄 형식 — 서버가 화면 안에서 여는 형식(`howto.controller.ts`의 INLINE_MIME)과 맞춘다. */
const INLINE_IMAGE = /^image\/(png|jpe?g|gif|webp|avif)$/;
const MAX_BYTES = 10 * 1024 * 1024;

/** 저장된 JSON 문서를 에디터 내용으로 푼다. 비었거나 깨졌으면 빈 문서로 연다. */
function parseBody(body: string): object | undefined {
  try {
    const parsed: unknown = JSON.parse(body);
    return typeof parsed === 'object' && parsed !== null && Object.keys(parsed).length > 0 ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * 노션 같은 블록 에디터 — `/`로 블록을 고르고, 마크다운 단축(`#`·`-`·`[]`·```` ``` ````)이 그대로 먹으며,
 * 이미지·파일은 붙여넣기·끌어놓기·`/`로 올린다. 문서는 JSON으로 내보낸다.
 *
 * 문서 하나당 인스턴스 하나다 — 부모가 `key`를 문서 id로 줘 문서를 바꾸면 새로 연다.
 */
export function HowtoEditor({
  body, onChange, onError,
}: {
  body: string;
  onChange: (json: string) => void;
  onError: (message: string) => void;
}): React.JSX.Element {
  const fileInput = useRef<HTMLInputElement>(null);
  const pickKind = useRef<'image' | 'file'>('file');
  const editorRef = useRef<Editor | null>(null);

  const insertUploaded = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        if (file.size > MAX_BYTES) {
          onError(`"${file.name}"은 10MB를 넘어 올릴 수 없다`);
          continue;
        }
        try {
          const uploaded = await howtoApi.upload(file);
          const chain = editorRef.current?.chain().focus();
          if (INLINE_IMAGE.test(uploaded.mimeType)) chain?.setImage({ src: uploaded.url, alt: uploaded.name }).run();
          else chain?.insertContent({ type: 'fileAttachment', attrs: { url: uploaded.url, name: uploaded.name, size: uploaded.size } }).run();
        } catch (cause) {
          onError(cause instanceof Error ? cause.message : String(cause));
        }
      }
    },
    [onError],
  );

  const extensions = useMemo(
    () => [
      StarterKit,
      Image.configure({ HTMLAttributes: { class: 'howto-image' } }),
      Placeholder.configure({ placeholder: "내용을 쓰거나 '/'로 블록을 고른다" }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      TaskList,
      TaskItem.configure({ nested: true }),
      FileAttachment,
      SlashMenu(
        slashCommands((kind) => {
          pickKind.current = kind;
          if (fileInput.current) fileInput.current.accept = kind === 'image' ? 'image/*' : '';
          fileInput.current?.click();
        }),
      ),
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: parseBody(body),
    editorProps: {
      attributes: { class: 'howto-doc outline-none' },
      handlePaste: (_view, event) => {
        const files = [...(event.clipboardData?.files ?? [])];
        if (files.length === 0) return false;
        void insertUploaded(files);
        return true;
      },
      handleDrop: (_view, event) => {
        const files = [...(event.dataTransfer?.files ?? [])];
        if (files.length === 0) return false;
        event.preventDefault();
        void insertUploaded(files);
        return true;
      },
    },
    onUpdate: ({ editor: current }) => onChange(JSON.stringify(current.getJSON())),
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  return (
    <>
      <EditorContent editor={editor} />
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          event.target.value = '';
          if (files.length > 0) void insertUploaded(files);
        }}
      />
    </>
  );
}
